import React, { useEffect, useState, useCallback } from "react";
import "react-table-v6/react-table.css";
import Sidebar from "../commonAdmin/Sidebar";
import Header from "../commonAdmin/Header";
import { Col, Container, Row, Table, Form } from "react-bootstrap";
import Footer from "../commonAdmin/Footer";
import Loader from "../../common/Loader";
import UpdateCurrencyRateModal from "./UpdateCurrencyRateModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import delivaApiService from "../../../common/services/apiService";
import {
  getCurrencySymbol,
  showSuccessToast,
  showErrorToast,
  getLocalStorage,
} from "../../../common/helpers/Utils";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { ReactComponent as CurrencyUpdateIcon } from "../../../assets/images/CurrencyUpdate.svg";

const PayoutManagement = (props) => {
  const [loading, setLoading] = useState(false);
  const { state } = useLocation();
  const [showCurrencyRateModal, setShowCurrencyRateModal] = useState(false);

  // Initialize directly from location state so the first API call uses the correct date
  const [exchangeRateDate, setExchangeRateDate] = useState(() => {
    if (state?.exchangeRateDate) {
      return moment(state.exchangeRateDate).format("YYYY-MM-DD");
    }
    return moment().format("YYYY-MM-DD");
  });
  const [fromCountry, setFromCountry] = useState(() => {
    if (state?.internationalPayoutRoute === "NIGERIA_TO_US") return "Naira";
    return "USD";
  });
  const [fromAmount, setFromAmount] = useState("0");
  const [toCountry, setToCountry] = useState(() => {
    if (state?.internationalPayoutRoute === "NIGERIA_TO_US") return "USD";
    return "Naira";
  });
  const [toAmount, setToAmount] = useState("0");

  // Table & Pagination State
  const [payoutList, setPayoutList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchKey, setSearchKey] = useState(""); // committed search value that triggers API
  const [pageno, setPageno] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [pageArray, setPageArray] = useState([]);
  const [records, setRecords] = useState(10);
  const [inputRecords, setInputRecords] = useState(10);
  const [sortBy, setSortBy] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [totalRecords, setTotalRecords] = useState(0);
  const [exchangeData, setExchangeData] = useState(null);

  // Row Selection State
  const [selectedRows, setSelectedRows] = useState([]);

  // Pay confirmation modal state
  const [payModal, setPayModal] = useState(false);
  const [payModalItem, setPayModalItem] = useState(null); // null = bulk, object = single
  const [payDisable, setPayDisable] = useState(false);

  // Wrapped in useCallback to prevent recreated function triggers
  const getPayoutList = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        roleId: 1,
        pageNo: pageno,
        pageSize: records,
        sortBy: sortBy,
        sortOrder: sortOrder,
        searchKey: searchKey,
        listFor: "international",
        deliveredOn: exchangeRateDate
          ? moment(exchangeRateDate).format("YYYY-MM-DD")
          : "",
        sourceCurrency: fromCountry === "Naira" ? "NGN" : "USD",
        destinationCurrency: toCountry === "Naira" ? "NGN" : "USD",
      };
      const { data: response } = await delivaApiService(
        "getListOfCompletedInternationalOrdersToBePaid",
        payload,
      );

      if (response?.status === 200) {
        let data = response?.resourceData;
        setPayoutList(data || []);
        setExchangeData({
          dailyHeaderRate: response?.dailyHeaderRate || 0,
          noOfRecords: response?.noOfRecords,
          totalConvertedAmount: response?.totalConvertedAmount,
          totalConvertedCurrency: response?.totalConvertedCurrency,
          totalRecords: response?.totalRecords,
          totalSourceAmount: response?.totalSourceAmount,
          totalSourceCurrency: response?.totalSourceAmount,
        });
        setFromAmount(response?.totalSourceAmount || 0);
        setToAmount(response?.totalConvertedAmount || 0);

        const total = response?.totalRecords || 0;
        const totalPages = records ? Math.ceil(total / records) : 1;

        setTotalRecords(total);
        setTotalPage(totalPages);
        setPageArray(generatePageArray(totalPages, pageno));
      }
    } catch (error) {
      console.error("Error fetching payout list:", error);
    } finally {
      setLoading(false);
    }
  }, [
    pageno,
    records,
    sortBy,
    sortOrder,
    searchKey,
    exchangeRateDate,
    fromCountry,
    toCountry,
  ]);

  // Single effect trigger for table reload when parameters change
  useEffect(() => {
    getPayoutList();
  }, [getPayoutList]);

  // Pagination Generator
  const generatePageArray = (totalPages, currentPage) => {
    let startPage, endPage;
    switch (true) {
      case totalPages <= 5:
        startPage = 1;
        endPage = totalPages;
        break;
      case currentPage <= 3:
        startPage = 1;
        endPage = 5;
        break;
      case currentPage + 1 >= totalPages:
        startPage = totalPages - 4;
        endPage = totalPages;
        break;
      default:
        startPage = currentPage - 2;
        endPage = currentPage + 2;
        break;
    }
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Handlers
  const onChangePage = (page) => {
    if (page >= 1 && page <= totalPage) {
      setPageno(page);
    }
  };

  const onChangeItemPerPage = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "" || /^[1-9][0-9]*$/.test(rawValue)) {
      setInputRecords(rawValue);
    }
  };

  const onEnterPerPage = (e) => {
    if (e.key === "Enter") {
      const val = inputRecords;
      if (val && val > 0) {
        setRecords(parseInt(val, 10));
        setPageno(1);
      }
    }
  };

  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(column);
      setSortOrder("ASC");
    }
  };

  const getRowId = (item) => item?.requestId || item?.id;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = payoutList.map(getRowId);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    const trimmedValue = value.replace(/\s+/g, " ").trimStart();
    setSearchQuery(trimmedValue);
    if (trimmedValue === "" && value?.[0] !== " ") {
      setPageno(1);
      setSearchKey("");
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setPageno(1);
      setSearchKey(searchQuery.trim());
    }
  };

  const handleSearchClick = () => {
    setPageno(1);
    setSearchKey(searchQuery.trim());
  };

  const handleBulkPayout = () => {
    setPayModalItem(null); // null signals bulk
    setPayModal(true);
  };

  const confirmBulkPayout = async () => {
    try {
      setPayDisable(true);
      setLoading(true);
      const selectedItems = payoutList.filter((item) =>
        selectedRows.includes(getRowId(item)),
      );
      const transferList = selectedItems.map((item) => ({
        amount: item?.convertedAmount ?? 0,
        currency: item?.convertedCurrency ?? "",
        userId: item?.partnerUserId,
        deliveryRequestId: item?.deliveryRequestId || item?.requestId,
        bidId: item?.bidId,
        transactionCycle: item?.transactionCycle ?? 1,
        exchangeRateUsed: exchangeData?.dailyHeaderRate ?? 0,
        exchangeRateSource: "ADMIN_OVERRIDE",
        sourceAmount: item?.sourceAmount ?? 0,
        convertedAmount: item?.convertedAmount ?? 0,
      }));
      const { data: response } = await delivaApiService("transferAllFunds", {
        transferList,
      });
      if (response?.status === 200) {
        showSuccessToast(response?.responseMessage || "Payout successful");
        setSelectedRows([]);
        setPayModal(false);
        getPayoutList();
      } else {
        showErrorToast(response?.message || "Payout failed");
      }
    } catch (error) {
      showErrorToast(error?.message || "Something went wrong");
    } finally {
      setPayDisable(false);
      setLoading(false);
    }
  };

  const handleCloseCurrencyRateModal = () => {
    setShowCurrencyRateModal(false);
  };

  const handleUpdateRate = (data) => {
    setShowCurrencyRateModal(false);
  };

  const handleIndividualPay = (item) => {
    setPayModalItem(item);
    setPayModal(true);
  };

  const confirmIndividualPay = async () => {
    const item = payModalItem;
    if (!item) return;
    try {
      setPayDisable(true);
      setLoading(true);
      const payload = {
        amount: item?.convertedAmount ?? 0,
        currency: item?.convertedCurrency ?? "",
        userId: item?.partnerUserId,
        deliveryRequestId: item?.deliveryRequestId || item?.requestId,
        bidId: item?.bidId,
        transactionCycle: item?.transactionCycle ?? 1,
        adminUserId: getLocalStorage("id"),
        isAutoScheduled: false,
      };

      const { data: response } = await delivaApiService(
        "transferFunds",
        payload,
      );
      if (response?.status === 200) {
        setPayModal(false);
        setPayModalItem(null);
        getPayoutList();
      } else {
        showErrorToast(response?.message || "Payout failed");
      }
    } catch (error) {
      showErrorToast(error?.message || "Something went wrong");
    } finally {
      setPayDisable(false);
      setLoading(false);
    }
  };

  const handleFromCurrencyChange = (currency) => {
    setFromCountry(currency);
    setToCountry(currency === "USD" ? "Naira" : "USD");
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };

  const handleToCurrencyChange = (currency) => {
    setToCountry(currency);
    setFromCountry(currency === "USD" ? "Naira" : "USD");
    setToAmount(fromAmount);
    setFromAmount(toAmount);
  };

  return (
    <div className="page__wrapper h-100">
      <Modal
        show={payModal}
        onHide={() => {
          setPayModal(false);
          setPayModalItem(null);
        }}
        centered
      >
        <Modal.Body className="p-4">
          <div className="text-center py-3">
            <CurrencyUpdateIcon className="img-fluid mb-3" />
            <h5 className="font-weight-medium mb-3" style={{ fontSize: "24px" }}>
              Confirm Payout
            </h5>
            <p className="text-muted mb-4" style={{ fontSize: "18px" }}>
              Are you sure you want to
              <br />
              make this payout?
            </p>
            <div
              className="d-flex justify-content-center"
              style={{ gap: "16px" }}
            >
              <button
                className="btnDefault btnOutline"
                style={{
                  minWidth: "140px",
                  borderRadius: "12px",
                  padding: "12px 24px",
                }}
                onClick={() => {
                  setPayModal(false);
                  setPayModalItem(null);
                }}
              >
                Cancel
              </button>
              <button
                className="button btnPrimary"
                style={{
                  minWidth: "140px",
                  borderRadius: "12px",
                  padding: "12px 24px",
                }}
                disabled={payDisable}
                onClick={
                  payModalItem ? confirmIndividualPay : confirmBulkPayout
                }
              >
                Confirm
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Header {...props} />
      <div className="mainContainer CustomerTable">
        <Row noGutters className="h-100">
          <Sidebar {...props} />
          <Col md={9} lg={10} className="boxTyp3">
            <Container fluid>
              <div className="content-wrapper">
                <h3 className="font-weight-normal mb-3 line-ht fs-24 color-33 text-nowrap">
                  Payout Management
                </h3>

                <div className="admin-wrap card border-0 shadow-sm p-4 mb-4 bg-white">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <p className="text-muted mb-1 fs-14">
                        1 United States Dollar equals
                      </p>
                      <h2 className="font-weight-normal fs28 mb-0">
                        {`${exchangeData?.dailyHeaderRate || 0} Nigerian Naira`}
                      </h2>
                    </div>
                    <div>
                      <Form.Group className="custom-form international-date d-flex flex-column">
                        <DatePicker
                          autoComplete="off"
                          placeholderText="Select date"
                          showTimeCaption={false}
                          selected={
                            exchangeRateDate
                              ? moment(exchangeRateDate).toDate()
                              : null
                          }
                          maxDate={new Date()}
                          dateFormat="dd MMM yyyy"
                          name="internationalPayoutDate"
                          onFocus={(e) => e.target.blur()}
                          onChange={(date) => {
                            if (date) {
                              setExchangeRateDate(
                                moment(date).format("YYYY-MM-DD"),
                              );
                            }
                          }}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <hr className="my-3" />

                  <h6 className="font-weight-normal fs-24 mt-2 mb-3">
                    Currency conversion
                  </h6>
                  <Row className="align-items-center mb-3">
                    <Col md={3}>
                      <Form.Group className="custom-form mb-0">
                        <Form.Label className="fs20 text-muted">
                          From country
                        </Form.Label>
                        <Form.Control
                          as="select"
                          className="login-input"
                          value={fromCountry}
                          onChange={(e) =>
                            handleFromCurrencyChange(e.target.value)
                          }
                        >
                          <option value="Naira">Naira</option>
                          <option value="USD">USD</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-0">
                        <Form.Label className="fs20 text-muted">
                          Amount
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="login-input bg-light text-capitalize"
                          value={fromAmount}
                          disabled={true}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="custom-form mb-0">
                        <Form.Label className="fs20 text-muted">
                          To country
                        </Form.Label>
                        <Form.Control
                          as="select"
                          className="login-input"
                          value={toCountry}
                          onChange={(e) =>
                            handleToCurrencyChange(e.target.value)
                          }
                        >
                          <option value="USD">USD</option>
                          <option value="Naira">Naira</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-0">
                        <Form.Label className="fs20 text-muted">
                          Amount
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="login-input bg-light"
                          value={toAmount}
                          disabled={true}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="d-flex justify-content-between mb-3 align-items-center">
                  <div>
                    <h5 className="font-weight-normal fs-24 mb-0">
                      List of payout ({totalRecords})
                    </h5>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="position-relative mr-2">
                      <Form.Control
                        type="text"
                        placeholder="Search by Request ID, Location"
                        className="searchTxt minimizeSearch"
                        style={{ width: "250px" }}
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onKeyDown={handleSearchKeyDown}
                      />
                      <span
                        className="icon-search"
                        onClick={handleSearchClick}
                      ></span>
                    </div>
                    <button
                      className="button rounded-pill btnPrimary text-nowrap px-4 py-2"
                      disabled={selectedRows.length === 0}
                      onClick={handleBulkPayout}
                    >
                      Make payout
                    </button>
                  </div>
                </div>

                <Table responsive className="align-middle">
                  <thead className="bg-warning-light">
                    <tr>
                      <th style={{ width: "40px" }}>
                        <Form.Check
                          type="checkbox"
                          className="checkbox-green"
                          onChange={handleSelectAll}
                          checked={
                            payoutList?.length > 0 &&
                            selectedRows.length === payoutList.length
                          }
                        />
                      </th>
                      <th>
                        Request ID{" "}
                        <Link
                          className="icon-up-and-down color-light-grey"
                          onClick={() => handleSortChange("id")}
                        />
                      </th>
                      <th>
                        Partner name{" "}
                        <Link
                          className="icon-up-and-down color-light-grey"
                          onClick={() => handleSortChange("partnerName")}
                        />
                      </th>
                      <th>From</th>
                      <th>To</th>
                      <th>{fromCountry === "Naira" ? "Naira" : "USD"}</th>
                      <th>{toCountry === "Naira" ? "Naira" : "USD"}</th>
                      <th>Make payout</th>
                    </tr>
                  </thead>
                  <tbody className="ttl-order-table">
                    {payoutList?.length > 0 ? (
                      payoutList.map((item, index) => (
                        <tr key={getRowId(item) || index}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              className="checkbox-green"
                              checked={selectedRows.includes(getRowId(item))}
                              onChange={() => handleSelectRow(getRowId(item))}
                            />
                          </td>
                          <td>{`#${getRowId(item)}`}</td>
                          <td>{item?.title || "--"}</td>
                          <td>{item?.originatingLocation || "--"}</td>
                          <td>
                            {item?.destinationLocation || "--"}
                            {item?.dropOffCount > 0 ? (
                              <span className="drop-off-count">
                                +{item.dropOffCount}
                              </span>
                            ) : null}
                          </td>
                          <td>
                            {item?.sourceAmount
                              ? `${getCurrencySymbol(
                                  item?.sourceCurrency,
                                  props.countries,
                                )}${item?.sourceAmount}`
                              : "--"}
                          </td>
                          <td>
                            {item?.convertedAmount
                              ? `${getCurrencySymbol(
                                  item?.convertedCurrency,
                                  props.countries,
                                )}${item?.convertedAmount}`
                              : "--"}
                          </td>
                          <td>
                            <button
                              className="cancel-btn px-3 py-1 btn-sm"
                              onClick={() => handleIndividualPay(item)}
                              disabled={selectedRows.includes(getRowId(item))}
                            >
                              Pay
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {payoutList?.length > 0 && (
                  <nav
                    aria-label="Page navigation"
                    className="position-relative mt-3"
                  >
                    <ul className="pagination pg-blue deliva-pagination justify-content-end">
                      <li className="page-item">
                        <button
                          className="page-link rotate-180 control-btn"
                          aria-label="Previous"
                          onClick={() => onChangePage(pageno - 1)}
                          disabled={pageno === 1}
                        >
                          <span className="icon-next"></span>
                          <span className="sr-only">Previous</span>
                        </button>
                      </li>

                      {pageArray?.map((page, ind) => (
                        <li key={ind} className="page-item">
                          <a
                            className={`page-link ${
                              pageno === page ? "active" : ""
                            }`}
                            onClick={() => onChangePage(page)}
                          >
                            {page}
                          </a>
                        </li>
                      ))}

                      <li className="page-item">
                        <button
                          className="page-link control-btn"
                          aria-label="Next"
                          onClick={() => onChangePage(pageno + 1)}
                          disabled={pageno === totalPage}
                        >
                          <span className="icon-next"></span>
                          <span className="sr-only">Next</span>
                        </button>
                      </li>

                      <li className="page-value">
                        Enter Per Page{" "}
                        <input
                          type="text"
                          placeholder="10"
                          name="records"
                          value={inputRecords}
                          onChange={onChangeItemPerPage}
                          onKeyDown={onEnterPerPage}
                        />
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </Container>
            <Footer {...props} />
          </Col>
        </Row>
      </div>
      <Loader showLoader={loading} />

      <UpdateCurrencyRateModal
        show={showCurrencyRateModal}
        onHide={handleCloseCurrencyRateModal}
        onUpdateRate={handleUpdateRate}
        fromCountry={fromCountry}
        toCountry={toCountry}
        currentRate={exchangeData?.dailyHeaderRate}
      />
    </div>
  );
};

export default connect((state) => ({ countries: state.country.countries }))(
  PayoutManagement,
);
